import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Card, Container, ProgressBar, Spinner } from 'react-bootstrap';
import { useConfirmPayphonePaymentMutation } from '../../slices/payphoneApiSlice';
import { useValidateCartStockMutation, useGetCartQuery } from '../../slices/cartApiSlice';
import { useCreateOrderMutation, useValidateOrderStockMutation } from '../../slices/orderApiSlice';
import { setSelectedCompany } from '../../slices/cartSlice';
import { toast } from 'react-toastify';
import { groupOrderItems } from '../../utils/cartUtils';
import '../../assets/styles/order.screen.css';

const PROCESS_STEPS = {
    loadingCart: 20,
    validatingStock: 45,
    creatingOrder: 75,
    confirmingPayment: 92,
    done: 100,
};

const PayPhoneOrder = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { companiesByRuc } = useSelector((state) => state.companies || {});
    const cart = useSelector((state) => state.cart);
    const { activeOrder, selectedCompanyRuc, billingAddress } = cart;

    const ppId = searchParams.get('id');
    const ppClientTransactionId = searchParams.get('clientTransactionId');
    const retryContext = useMemo(() => {
        if (!ppClientTransactionId || typeof window === 'undefined') {
            return null;
        }

        const rawContext = window.sessionStorage.getItem(`payphoneContext:${ppClientTransactionId}`);
        if (!rawContext) {
            return null;
        }

        try {
            return JSON.parse(rawContext);
        } catch (error) {
            console.error('Invalid PayPhone retry context:', error);
            return null;
        }
    }, [ppClientTransactionId]);
    const isRetryFlow = retryContext?.flow === 'orderRetry' && Boolean(retryContext?.orderId);
    const retryOrderId = retryContext?.orderId || null;
    const retryCompanyRuc = retryContext?.companyRuc || null;

    // Ensure cart is loaded for standard checkout flow only
    const { isLoading: isCartLoading } = useGetCartQuery(undefined, { skip: isRetryFlow });

    const [ppDoConfirm] = useConfirmPayphonePaymentMutation();
    const [validateCartStock] = useValidateCartStockMutation();
    const [validateOrderStock] = useValidateOrderStockMutation();
    const [createOrder] = useCreateOrderMutation();
    const [verified, setVerified] = useState(false);
    const [processStep, setProcessStep] = useState('loadingCart');
    const [statusMessage, setStatusMessage] = useState('Preparando tu pedido seguro...');

    // Support both new object shape and legacy/server array shape
    const groupedItems = useMemo(() => {
        return groupOrderItems(activeOrder, companiesByRuc);
    }, [activeOrder, companiesByRuc]);

    const selectedGroup = selectedCompanyRuc ? groupedItems[selectedCompanyRuc] : null;

    useEffect(() => {
        if (!isRetryFlow && isCartLoading) {
            setProcessStep('loadingCart');
            setStatusMessage('Cargando informacion de tu carrito...');
            return;
        }

        const confirmPayment = async () => {
            if (isRetryFlow) {
                try {
                    setProcessStep('validatingStock');
                    setStatusMessage('Validando stock de tu pedido...');
                    const validationRes = await validateOrderStock(retryOrderId).unwrap();
                    const isValidationSuccessful =
                        validationRes.ok === true ||
                        validationRes.success === true ||
                        validationRes.valid === true;
                    const isValidationFailed = !isValidationSuccessful;

                    if (isValidationFailed) {
                        const errorMessage =
                            validationRes.message || 'Algunos productos ya no tienen stock disponible.';
                        toast.error(errorMessage);
                        navigate(`/order/${retryOrderId}?error=stock`);
                        return;
                    }

                    setProcessStep('confirmingPayment');
                    setStatusMessage('Confirmando pago con PayPhone...');
                    await ppDoConfirm({
                        id: ppId,
                        clientTransactionId: ppClientTransactionId,
                        orderId: retryOrderId,
                        companyRuc: retryCompanyRuc,
                    }).unwrap();

                    if (typeof window !== 'undefined') {
                        window.sessionStorage.removeItem(`payphoneContext:${ppClientTransactionId}`);
                    }

                    setProcessStep('done');
                    setStatusMessage('Pago confirmado. Redirigiendo al detalle de tu orden...');
                    toast.success('Pago exitoso! Tu pedido ha sido actualizado.');
                    navigate(`/order/${retryOrderId}`);
                    return;
                } catch (error) {
                    const errorMessage = error?.data?.message || error?.message || 'Error al confirmar el pago.';
                    toast.error(errorMessage);
                    navigate(`/order/${retryOrderId}?error=payment`);
                    return;
                }
            }

            if (!selectedCompanyRuc) {
                toast.error('Informacion de compania no encontrada.');
                navigate('/cart');
                return;
            }

            if (!selectedGroup) {
                toast.error('Informacion de orden invalida.');
                navigate('/cart');
                return;
            }

            try {
                setProcessStep('validatingStock');
                setStatusMessage('Validando stock en tiempo real...');
                const validationRes = await validateCartStock({ companyId: selectedCompanyRuc }).unwrap();

                const isValidationSuccessful =
                    validationRes.ok === true ||
                    validationRes.success === true ||
                    validationRes.valid === true;
                const isValidationFailed = !isValidationSuccessful;
                
                if (isValidationFailed) {
                    const errorMessage = validationRes.message || 'Algunos productos ya no están disponibles. Por favor, revisa tu carrito.';
                    console.error('Stock validation failed:', validationRes);
                    toast.error(errorMessage);
                    navigate('/cart');
                    return;
                }

                setProcessStep('creatingOrder');
                setStatusMessage('Creando tu orden y preparando facturacion...');
                const orderPayload = {
                    billingAddress,
                    paymentMethod: 'PayPhone',
                    observations: '',
                    companyRuc: selectedCompanyRuc,
                    fastPricing: false,
                    paymentId: ppClientTransactionId,
                };

                const orderRes = await createOrder(orderPayload).unwrap();
                const orderId = orderRes._id;

                setProcessStep('confirmingPayment');
                setStatusMessage('Confirmando pago con PayPhone...');
                await ppDoConfirm({
                    id: ppId,
                    clientTransactionId: ppClientTransactionId,
                    orderId,
                    companyRuc: selectedCompanyRuc,
                }).unwrap();

                dispatch(setSelectedCompany(null));

                setProcessStep('done');
                setStatusMessage('Pago confirmado. Redirigiendo al detalle de tu orden...');

                const isInvoiceReady = (() => {
                    const contifico = Array.isArray(orderRes?.contifico) ? orderRes.contifico[0] : orderRes?.contifico;
                    const rideUrl = contifico?.urlRide || contifico?.rideUrl || contifico?.raw?.url_ride;
                    const xmlUrl = contifico?.urlXml || contifico?.xmlUrl || contifico?.raw?.url_xml;
                    const hasSriAuthorizationData = Boolean(contifico?.raw?.autorizacion || contifico?.authorization);

                    return (
                        orderRes?.orderStatus === 'INVOICED' ||
                        contifico?.status === 'AUTHORIZED' ||
                        (Boolean(rideUrl || xmlUrl) && hasSriAuthorizationData)
                    );
                })();

                if (isInvoiceReady) {
                    toast.success('Pago exitoso! Tu factura esta lista.');
                } else {
                    toast.success(
                        'Pago exitoso! Tu factura esta siendo procesada por el SRI (hasta 24h). Revisa "Mis Pedidos" para actualizaciones.'
                    );
                }

                navigate(`/order/${orderId}`);
            } catch (error) {
                console.error('PayPhone confirmation error:', error);
                
                // Determine error type and show appropriate message
                let errorMessage = 'Error al confirmar el pago.';
                
                if (error?.data?.message) {
                    errorMessage = error.data.message;
                } else if (error?.message) {
                    errorMessage = error.message;
                }
                
                console.error('Error details:', {
                    status: error?.status,
                    data: error?.data,
                    message: errorMessage,
                });
                
                toast.error(errorMessage);
                navigate('/cart');
            }
        };

        if (!verified && ppId && ppClientTransactionId) {
            setVerified(true);
            confirmPayment();
        }
    }, [
        ppId,
        ppClientTransactionId,
        selectedCompanyRuc,
        selectedGroup,
        dispatch,
        navigate,
        ppDoConfirm,
        validateCartStock,
        validateOrderStock,
        createOrder,
        verified,
        isCartLoading,
        billingAddress,
        isRetryFlow,
        retryOrderId,
        retryCompanyRuc,
    ]);

    return (
        <Container className="payphone-loader-page">
            <Card className="payphone-loader-card shadow-sm border-0">
                <Card.Body className="p-4 p-md-5">
                    <div className="d-flex justify-content-center mb-3">
                        <div className="payphone-loader-spinner-wrap">
                            <Spinner animation="border" role="status" className="payphone-loader-spinner" />
                        </div>
                    </div>

                    <h3 className="text-center fw-bold mb-2">Estamos procesando tu pago</h3>
                    <p className="text-center text-muted mb-4" aria-live="polite">
                        {statusMessage}
                    </p>

                    <ProgressBar
                        now={PROCESS_STEPS[processStep] || PROCESS_STEPS.loadingCart}
                        className="payphone-loader-progress"
                    />

                    <div className="payphone-loader-steps mt-4">
                        <div className={processStep === 'loadingCart' ? 'active' : ''}>1. Cargar carrito</div>
                        <div className={processStep === 'validatingStock' ? 'active' : ''}>2. Validar stock</div>
                        <div className={processStep === 'creatingOrder' ? 'active' : ''}>3. Crear orden</div>
                        <div className={processStep === 'confirmingPayment' ? 'active' : ''}>4. Confirmar pago</div>
                    </div>

                    <p className="small text-muted text-center mt-4 mb-0">
                        No cierres esta ventana. Te redirigiremos automaticamente en unos segundos.
                    </p>
                </Card.Body>
            </Card>
        </Container>
    );
};

export default PayPhoneOrder;
